import { t } from "@lingui/macro";
import { ResumeDto } from "@reactive-resume/dto";
import { defaultSections } from "@reactive-resume/schema";
import { useCallback, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { LoaderFunction, redirect } from "react-router-dom";

import { queryClient } from "@/client/libs/query-client";
import { findResumeById } from "@/client/services/resume";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore } from "@/client/stores/resume";

import { repairResumeItemIds } from "./repair-resume-ids";

const normalizeResumeSections = (resume: ResumeDto): ResumeDto => {
  const normalized: ResumeDto = {
    ...resume,
    data: {
      ...resume.data,
      sections: {
        ...defaultSections,
        ...resume.data.sections,
        custom: resume.data.sections.custom ?? defaultSections.custom,
      },
    },
  };

  /**
   * ⚠️ MAKES AN OTHERWISE UNSAVABLE RÉSUMÉ SAVABLE AGAIN.
   *
   * `PATCH /resume/:id` validates the whole document, and every item id is `z.string().cuid2()`. One
   * malformed id therefore rejects EVERY write to that résumé rather than degrading — the student
   * sees a résumé that renders fine and a save that silently does nothing. See repair-resume-ids.ts
   * for where the bad ids came from and why rewriting them is safe.
   *
   * The spread above is SHALLOW, so this also repairs the section objects inside react-query's
   * `["resume", { id }]` entry — they are the same references. That is wanted, not tolerated: the
   * cached copy feeds the résumé list and the AI optimization page's dropdown, and none of them have
   * any use for the broken ids. What must not happen is the two disagreeing, which sharing prevents.
   */
  const repaired = repairResumeItemIds(normalized.data);

  if (repaired > 0) {
    /* eslint-disable lingui/no-unlocalized-strings -- operator-facing diagnostic, not UI copy */
    console.warn(
      `[Resume] Replaced ${repaired} item id(s) that were not valid cuid2. Every save on this resume was being rejected as a 400 Validation failed until now.`,
    );
    /* eslint-enable lingui/no-unlocalized-strings */
  }

  return normalized;
};

export const BuilderPage = () => {
  const frameRef = useBuilderStore((state) => state.frame.ref);
  const setFrameRef = useBuilderStore((state) => state.frame.setRef);

  const resume = useResumeStore((state) => state.resume);
  const title = useResumeStore((state) => state.resume.title);

  const updateResumeInFrame = useCallback(() => {
    if (!frameRef || !frameRef.contentWindow) return;
    const message = { type: "SET_RESUME", payload: resume.data };
    (() => frameRef.contentWindow.postMessage(message, "*"))();
  }, [frameRef, resume.data]);

  // Send resume data as soon as the artboard says its message listener is live.
  //
  // THIS is the reliable trigger, and the reason the preview used to come up blank
  // until a reload. `SET_RESUME` is fire-and-forget with no replay, and the two
  // triggers below can both fire too early:
  //   - the `load` event fires when the iframe document finishes loading, but the
  //     artboard registers its listener in a passive effect that React may schedule
  //     after that, so the message lands with nobody listening;
  //   - `frame.ref` lives in zustand rather than a useRef, so on first render it is
  //     still null and the data effect bails; it only re-runs once the ref arrives.
  // Whether either window opened was down to timing — a warm cache for /artboard/builder
  // or a busy main thread (which is exactly the state the coach intake navigates in)
  // was enough to lose the payload permanently.
  useEffect(() => {
    const onArtboardReady = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === "ARTBOARD_READY") updateResumeInFrame();
    };

    window.addEventListener("message", onArtboardReady);
    return () => window.removeEventListener("message", onArtboardReady);
  }, [updateResumeInFrame]);

  // Send resume data to iframe on initial load. Retained as a second path for an
  // artboard build that predates ARTBOARD_READY; harmless when both fire, since
  // setResume with the same data is idempotent.
  useEffect(() => {
    if (!frameRef) return;
    frameRef.addEventListener("load", updateResumeInFrame);
    return () => frameRef.removeEventListener("load", updateResumeInFrame);
  }, [frameRef, updateResumeInFrame]);

  // Send resume data to iframe on change of resume data.
  //
  // `frameRef` is in the deps as well: the loader hydrates the store before this
  // component first renders, so `resume.data` is referentially unchanged between the
  // null-ref render and the one that has the ref, and this would never re-run.
  useEffect(updateResumeInFrame, [frameRef, resume.data]);

  return (
    <>
      <Helmet>
        <title>
          {title} - {t`Coach LMS`}
        </title>
      </Helmet>

      <iframe
        ref={setFrameRef}
        title={resume.id}
        src="/artboard/builder"
        className="mt-16 w-screen"
        style={{ height: `calc(100vh - 64px)` }}
      />
    </>
  );
};

export const builderLoader: LoaderFunction<ResumeDto> = async ({ params }) => {
  try {
    const id = params.id as string;

    const resume = await queryClient.fetchQuery({
      queryKey: ["resume", { id }],
      queryFn: () => findResumeById({ id }),
    });

    useResumeStore.setState({ resume: normalizeResumeSections(resume) });
    useResumeStore.temporal.getState().clear();

    return resume;
  } catch (error) {
    return redirect("/dashboard");
  }
};
